import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, FileText } from "lucide-react";
import seoRoles, { getSeoRole } from "../data/seoRoles";

const siteUrl = "https://deep-letter.com";

function CoverLetterSEO() {
  const { role: roleSlug } = useParams();
  const role = getSeoRole(roleSlug);

  useEffect(() => {
    if (!role) return;

    document.title = `${role.title} | DEEP-LETTER`;

    let description = document.querySelector('meta[name="description"]');
    if (!description) {
      description = document.createElement("meta");
      description.name = "description";
      document.head.appendChild(description);
    }
    description.content = role.description;

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${siteUrl}/cover-letter/${role.slug}`;

    return () => {
      document.title = "DEEP-LETTER";
      description.content =
        "Deep Reasoning AI Cover Letter Generator & Resume ATS Scanner";
      canonical.remove();
    };
  }, [role]);

  if (!role) {
    return <Navigate to="/" replace />;
  }

  const relatedRoles = seoRoles
    .filter((candidate) => candidate.slug !== role.slug)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-gray-100 text-gray-800">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link
            to="/"
            className="font-bold tracking-widest text-gray-700"
            aria-label="DEEP-LETTER home"
          >
            DEEP-LETTER
          </Link>
          <Link to="/" className="btn btn-neutral btn-sm">
            Create my cover letter
          </Link>
        </div>
      </header>

      <main>
        <article>
          <section className="bg-white">
            <div className="mx-auto max-w-4xl px-5 py-12 text-center md:py-16">
              <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-gray-500">
                Role-specific cover letter guide
              </p>
              <h1 className="text-3xl font-bold leading-tight text-gray-900 md:text-5xl">
                {role.title}
              </h1>
              <p className="mx-auto mt-5 max-w-3xl text-base leading-7 text-gray-600 md:text-lg">
                {role.intro}
              </p>
              <Link to="/" className="btn btn-primary mt-7">
                Write a tailored cover letter
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </section>

          <div className="mx-auto grid max-w-6xl gap-6 px-5 py-10 lg:grid-cols-[minmax(0,1fr)_20rem]">
            <div className="space-y-6">
              <section className="card border border-gray-200 bg-white shadow-sm">
                <div className="card-body">
                  <h2 className="card-title text-2xl">
                    Skills to emphasize
                  </h2>
                  <p className="text-gray-600">
                    Prioritize skills that are both true of your experience and
                    relevant to the specific job description.
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-2" aria-label="Common skills">
                    {role.skills.map((skill) => (
                      <li key={skill} className="badge badge-outline p-3">
                        {skill}
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              <section className="card border border-gray-200 bg-white shadow-sm">
                <div className="card-body">
                  <h2 className="card-title text-2xl">
                    How to tailor your letter
                  </h2>
                  <ul className="mt-2 space-y-3">
                    {role.tips.map((tip) => (
                      <li key={tip} className="flex gap-3 leading-7 text-gray-700">
                        <span
                          className="mt-2 h-2 w-2 shrink-0 rounded-full bg-gray-800"
                          aria-hidden="true"
                        />
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>

              <section className="card border border-gray-200 bg-white shadow-sm">
                <div className="card-body">
                  <div className="flex items-center gap-3">
                    <FileText className="h-6 w-6" aria-hidden="true" />
                    <h2 className="card-title text-2xl">
                      {role.title.replace("Example & Tips", "Example")}
                    </h2>
                  </div>
                  <div className="mt-3 whitespace-pre-line rounded-lg border border-gray-200 bg-gray-50 p-5 leading-7 text-gray-700">
                    {role.exampleCoverLetter}
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Use this example for structure and tone. Replace every
                    detail with your own truthful experience and results.
                  </p>
                </div>
              </section>

              <section className="card border border-gray-200 bg-white shadow-sm">
                <div className="card-body">
                  <h2 className="card-title text-2xl">Frequently asked questions</h2>
                  <div className="mt-2 divide-y divide-gray-200">
                    {role.faq.map((item) => (
                      <details key={item.question} className="py-4">
                        <summary className="cursor-pointer font-semibold text-gray-900">
                          {item.question}
                        </summary>
                        <p className="mt-2 leading-7 text-gray-600">{item.answer}</p>
                      </details>
                    ))}
                  </div>
                </div>
              </section>
            </div>

            <aside className="space-y-6 lg:sticky lg:top-5 lg:self-start">
              <section className="card bg-neutral text-neutral-content shadow-lg">
                <div className="card-body">
                  <h2 className="card-title">Make this letter yours</h2>
                  <p className="text-sm leading-6 text-gray-200">
                    DEEP-LETTER reads your resume and job description,
                    identifies the employer, researches current public company
                    information such as products and recent launches, and uses
                    that context to write a more tailored cover letter.
                  </p>
                  <div className="card-actions mt-2">
                    <Link to="/" className="btn btn-primary w-full">
                      Use DEEP-LETTER
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </section>

              <nav
                className="card border border-gray-200 bg-white shadow-sm"
                aria-labelledby="related-role-heading"
              >
                <div className="card-body">
                  <h2 id="related-role-heading" className="card-title text-lg">
                    Related role examples
                  </h2>
                  <ul className="space-y-3 text-sm">
                    {relatedRoles.map((relatedRole) => (
                      <li key={relatedRole.slug}>
                        <Link
                          to={`/cover-letter/${relatedRole.slug}`}
                          className="link link-hover"
                        >
                          {relatedRole.title.replace(" Example & Tips", "")}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </nav>
            </aside>
          </div>
        </article>
      </main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-6xl px-5 py-6 text-center text-sm text-gray-500">
          <Link to="/" className="link link-hover">
            Create a tailored cover letter with DEEP-LETTER
          </Link>
        </div>
      </footer>
    </div>
  );
}

export default CoverLetterSEO;
